'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';

interface ResetFormData {
  email: string;
}

export default function PasswordResetForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResetFormData>({ mode: 'onBlur' });

  const onSubmit = async (data: ResetFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/seller-dashboard`,
      });

      if (error) {
        setError('root', { message: error.message || 'Failed to send reset email. Please try again.' });
        setIsLoading(false);
        return;
      }

      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('root', { message: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-card border border-border rounded-2xl shadow-card p-8 text-center">
        {/* Success icon */}
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg
            className="w-8 h-8 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-700 text-foreground mb-2">
          Check Your Email
        </h2>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
          We sent a password reset link to
        </p>
        <div className="bg-muted/50 border border-border rounded-xl px-4 py-3 mb-6">
          <p className="text-sm font-600 text-foreground">{submittedEmail}</p>
        </div>

        <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
          The link expires in 1 hour. If you don&apos;t see the email, check
          your spam folder or try again with a different address.
        </p>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setSubmittedEmail('');
            }}
            className="w-full border border-border rounded-xl py-2.5 text-sm font-600 text-secondary-foreground hover:bg-muted transition-colors"
          >
            Try a different email
          </button>
          <Link
            href="/seller-login"
            className="block w-full btn-primary py-2.5 rounded-xl text-sm font-600 text-center"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-8 space-y-5">
        {/* Root error */}
        {errors.root && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errors.root.message}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            Email Address
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Enter the email you used to register your seller account
          </p>
          <input
            type="email"
            placeholder="rahul@yourstore.in"
            autoComplete="email"
            className="w-full border border-input rounded-xl px-4 py-2.5 text-sm text-foreground bg-card input-focus placeholder:text-muted-foreground"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="text-xs text-error mt-1.5 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[46px]"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              Sending reset link…
            </>
          ) : (
            <>
              <Icon name="EnvelopeIcon" size={16} />
              Send Reset Link
            </>
          )}
        </button>

        {/* Back to login */}
        <div className="text-center">
          <Link
            href="/seller-login"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
          >
            <Icon name="ArrowLeftIcon" size={14} />
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );
}